import { __ } from '@wordpress/i18n';
import { Input, Select, Label, Checkbox } from '@bsf/force-ui';
import { z } from 'zod';
import TruncateText from '@components/truncate-text';
import { HELP_TEXTS } from '@forms/constants';

const SHOW_MORE_CHARACTER_LIMIT = 50;

const encryptionOptions = {
	TLS: 'TLS',
	SSL: 'SSL',
	NONE: 'None',
};

export const osmtpDefaultValues = {
	host: '',
	port: '',
	encryption: 'TLS',
	auto_tls: 'yes',
	auth: 'yes',
	username: '',
	password: '',
	from_email: '',
	from_name: '',
	force_from_name: false,
	force_from_email: true,
	return_path: true,
	type: 'SMTP',
	id: '',
	priority: 1,
	connection_title: '',
};

// Validation Schema for OSMTP Form.
export const osmtpFormSchema = z.object( {
	connection_title: z
		.string()
		.min( 1, __( 'Connection title is required', 'suremails' ) ),
	priority: z
		.number()
		.min( 1, __( 'Priority must be at least 1', 'suremails' ) ),
	host: z.string().min( 1, __( 'SMTP host is required', 'suremails' ) ),
	port: z.number().min( 1, __( 'SMTP port is required', 'suremails' ) ),
	encryption: z.string(),
	auto_tls: z.string(),
	auth: z.string(),
	username: z.string().min( 1, __( 'Username is required', 'suremails' ) ),
	password: z.string().min( 1, __( 'Password is required', 'suremails' ) ),
	from_email: z
		.string()
		.email( __( 'Please enter a valid email address', 'suremails' ) ),
	from_name: z.string().optional(),
	force_from_name: z.boolean(),
	force_from_email: z.boolean(),
	return_path: z.boolean(),
	type: z.string(),
	id: z.string(),
} );

const OSMTPForm = ( { connectionData, onChange, errors, inlineValidator } ) => {
	const handleChange = ( field, value ) => {
		onChange?.( { [ field ]: value } );
	};

	return (
		<div className="flex flex-col gap-4">
			{ /* Connection Title */ }
			<div className="flex flex-col py-2 pl-2">
				<Input
					name="connection_title"
					type="text"
					size="md"
					value={ connectionData?.connection_title || '' }
					onChange={ ( value ) =>
						handleChange( 'connection_title', value )
					}
					onBlur={ inlineValidator }
					error={ errors?.connection_title }
					placeholder={ __(
						'Enter a title for this connection',
						'suremails'
					) }
					label={ __( 'Connnection Title', 'suremails' ) }
					className="w-full"
					required
				/>
			</div>

			{ /* Priority */ }
			<div className="flex flex-col gap-1.5 py-2 pl-2">
				<Input
					name="priority"
					type="number"
					size="md"
					value={ connectionData?.priority }
					onChange={ ( value ) =>
						handleChange( 'priority', parseInt( value ) )
					}
					onBlur={ inlineValidator }
					error={ errors?.priority }
					placeholder={ __( 'Enter a priority number', 'suremails' ) }
					label={ __( 'Connection Sequence', 'suremails' ) }
					className="w-full"
					required
					min={ 1 }
				/>
				<Label tag="p" size="sm" variant="help">
					<TruncateText
						text={ HELP_TEXTS.CONNECTION_SEQUENCE }
						characterLimit={ SHOW_MORE_CHARACTER_LIMIT }
					/>
				</Label>
			</div>

			{ /* Host */ }
			<div className="flex flex-col py-2 pl-2">
				<Input
					name="host"
					type="text"
					size="md"
					value={ connectionData?.host }
					onChange={ ( value ) => handleChange( 'host', value ) }
					onBlur={ inlineValidator }
					error={ errors?.host }
					placeholder={ __( 'Enter the SMTP host', 'suremails' ) }
					label={ __( 'Host', 'suremails' ) }
					required
					className="w-full"
				/>
			</div>

			{ /* Port */ }
			<div className="flex flex-col py-2 pl-2">
				<Input
					name="port"
					type="number"
					size="md"
					value={ connectionData?.port }
					onChange={ ( value ) =>
						handleChange( 'port', parseInt( value ) )
					}
					onBlur={ inlineValidator }
					error={ errors?.port }
					placeholder={ __( 'Enter the SMTP port', 'suremails' ) }
					label={ __( 'Port', 'suremails' ) }
					className="w-full"
					required
				/>
			</div>

			{ /* Encryption */ }
			<div className="flex flex-col gap-1.5 py-2 pl-2">
				<Label size="sm">{ __( 'Encryption', 'suremails' ) }</Label>
				<Select
					value={ connectionData?.encryption }
					onChange={ ( value ) =>
						handleChange( 'encryption', value )
					}
					className="w-full h-[40px]"
				>
					<Select.Button type="button" />
					<Select.Options
						dropdownPortalRoot="suremails-root-app"
						dropdownPortalId="suremails-root-app"
						className="z-999999"
					>
						{ Object.keys( encryptionOptions ).map( ( key ) => (
							<Select.Option key={ key } value={ key }>
								{ encryptionOptions[ key ] }
							</Select.Option>
						) ) }
					</Select.Options>
				</Select>
				<Label tag="p" size="sm" variant="help">
					{ __(
						'Choose SSL for port 465, or TLS for port 25 or 587',
						'suremails'
					) }
				</Label>
			</div>

			{ /* Username */ }
			<div className="flex flex-col py-2 pl-2">
				<Input
					name="username"
					type="text"
					size="md"
					value={ connectionData?.username }
					onChange={ ( value ) => handleChange( 'username', value ) }
					onBlur={ inlineValidator }
					error={ errors?.username }
					placeholder={ __(
						'Enter your SMTP username',
						'suremails'
					) }
					label={ __( 'Username', 'suremails' ) }
					required
					className="w-full"
				/>
			</div>

			{ /* Password */ }
			<div className="flex flex-col py-2 pl-2">
				<Input
					name="password"
					type="password"
					size="md"
					value={ connectionData?.password }
					onChange={ ( value ) => handleChange( 'password', value ) }
					onBlur={ inlineValidator }
					error={ errors?.password }
					placeholder={ __(
						'Enter your SMTP password',
						'suremails'
					) }
					label={ __( 'Password', 'suremails' ) }
					required
					className="w-full"
				/>
			</div>

			{ /* Auto TLS */ }
			<div className="p-2">
				<Checkbox
					checked={ connectionData?.auto_tls === 'yes' }
					size="sm"
					onChange={ ( value ) =>
						handleChange( 'auto_tls', value ? 'yes' : 'no' )
					}
					label={ {
						heading: __( 'Auto TLS', 'suremails' ),
						description: __(
							'Enable TLS automatically if the server supports it.',
							'suremails'
						),
					} }
				/>
			</div>

			{ /* Authentication */ }
			<div className="hidden p-2">
				<Checkbox
					checked={ connectionData?.auth === 'yes' }
					size="sm"
					onChange={ ( value ) =>
						handleChange( 'auth', value ? 'yes' : 'no' )
					}
					label={ {
						heading: __( 'Authentication', 'suremails' ),
						description: __(
							'Require authentication for this connection',
							'suremails'
						),
					} }
				/>
			</div>

			{ /* From Email */ }
			<div className="flex-col flex-1 py-2 pl-2">
				<Input
					name="from_email"
					type="email"
					size="md"
					value={ connectionData?.from_email }
					onChange={ ( value ) =>
						handleChange( 'from_email', value )
					}
					onBlur={ inlineValidator }
					error={ errors?.from_email }
					placeholder={ __(
						'Enter the email address to send from',
						'suremails'
					) }
					label={ __( 'From Email', 'suremails' ) }
					required
					className="w-full"
				/>
			</div>
			<div className="p-2">
				<Checkbox
					checked={ connectionData?.force_from_email }
					size="sm"
					onChange={ ( value ) =>
						handleChange( 'force_from_email', value )
					}
					label={ {
						heading: __( 'Force From Email', 'suremails' ),
						description: (
							<TruncateText
								text={ HELP_TEXTS.FORCE_FROM_EMAIL }
								characterLimit={ SHOW_MORE_CHARACTER_LIMIT }
							/>
						),
					} }
				/>
			</div>

			{ /* Return Path */ }
			<div className="p-2">
				<Checkbox
					checked={ connectionData?.return_path }
					size="sm"
					onChange={ ( value ) =>
						handleChange( 'return_path', value )
					}
					label={ {
						heading: __( 'Return Path', 'suremails' ),
						description: (
							<TruncateText
								text={ HELP_TEXTS.RETURN_PATH }
								characterLimit={ SHOW_MORE_CHARACTER_LIMIT }
							/>
						),
					} }
				/>
			</div>

			{ /* From Name */ }
			<div className="flex flex-col py-2 pl-2">
				<Input
					name="from_name"
					type="text"
					size="md"
					value={ connectionData?.from_name }
					onChange={ ( value ) => handleChange( 'from_name', value ) }
					onBlur={ inlineValidator }
					error={ errors?.from_name }
					placeholder={ __(
						'Enter the name to send from',
						'suremails'
					) }
					label={ __( 'From Name', 'suremails' ) }
					className="w-full"
				/>
			</div>
			<div className="p-2">
				<Checkbox
					checked={ connectionData?.force_from_name }
					size="sm"
					onChange={ ( value ) =>
						handleChange( 'force_from_name', value )
					}
					label={ {
						heading: __( 'Force From Name', 'suremails' ),
						description: (
							<TruncateText
								text={ HELP_TEXTS.FORCE_FROM_NAME }
								characterLimit={ SHOW_MORE_CHARACTER_LIMIT }
							/>
						),
					} }
					disabled={ ! connectionData?.from_name }
				/>
			</div>
		</div>
	);
};

export default OSMTPForm;
